import React, { useEffect, useMemo, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const MAP_STYLE_URL = process.env.REACT_APP_MAP_STYLE_URL || 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';
const MAP_STYLE_URL_DARK = process.env.REACT_APP_MAP_STYLE_URL_DARK || 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const ROUTE_SOURCE_ID = 'route-line-source';
const ROUTE_LAYER_ID = 'route-line-layer';

const MARKER_ICONS = {
  courier: '<svg viewBox="0 0 64 72" width="40" height="46" style="display:block;" aria-hidden="true"><defs><linearGradient id="pinOuter" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#0f172a"/><stop offset="100%" stop-color="#020617"/></linearGradient><linearGradient id="pinInner" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#60a5fa"/><stop offset="100%" stop-color="#0ea5e9"/></linearGradient><linearGradient id="pinTip" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fb923c"/><stop offset="100%" stop-color="#ea580c"/></linearGradient></defs><path d="M32 2C17.6 2 6 13.6 6 28c0 17.7 18.2 35.8 24.8 41.8.7.6 1.7.6 2.4 0C39.8 63.8 58 45.7 58 28 58 13.6 46.4 2 32 2z" fill="url(#pinOuter)" stroke="#dbeafe" stroke-width="2"/><circle cx="32" cy="28" r="20.5" fill="url(#pinInner)" stroke="#e2e8f0" stroke-width="1.4"/><path d="M27.7 41.8a4.2 4.2 0 110-8.4 4.2 4.2 0 010 8.4zm13 0a4.2 4.2 0 110-8.4 4.2 4.2 0 010 8.4z" fill="#0f172a"/><path d="M31.8 37.6h3.8l2.9-5.2h5.1" fill="none" stroke="#0f172a" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/><path d="M36.4 32.4l-2.5-4.4h-5.1" fill="none" stroke="#0f172a" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/><rect x="38" y="24.8" width="6.1" height="4.1" rx="0.9" fill="#f97316" stroke="#7c2d12" stroke-width="1"/><circle cx="35.1" cy="22.6" r="3.8" fill="#ef4444"/><path d="M31 50.5l-6.8 10.7L32 68l7.8-6.8L33 50.5a1 1 0 00-2 0z" fill="url(#pinTip)" stroke="#fdba74" stroke-width="1"/></svg>',
  pickup: '&#127979;', // convenience store
  restaurant: '&#127979;',
  delivery_home: '&#127968;', // house
  delivery_work: '&#127970;', // office building
  customer: '&#127968;',
  default: '&#128204;', // pin
};

function isValidCoord(value) {
  return Number.isFinite(value);
}

function toLngLat(coords) {
  if (!coords) return null;
  if (!isValidCoord(coords.lat) || !isValidCoord(coords.lng)) return null;
  return [coords.lng, coords.lat];
}

function normalizeRoute(route) {
  if (!route) return null;
  if (route.type === 'Feature') return route;
  if (route.type && route.coordinates) {
    return { type: 'Feature', geometry: route, properties: {} };
  }
  if (route.geometry) {
    return { type: 'Feature', geometry: route.geometry, properties: {} };
  }
  return null;
}

function buildMarkerElement({ kind, label, color }) {
  const wrapper = document.createElement('div');
  wrapper.className = `map-marker map-marker--${kind || 'default'}`;
  wrapper.style.setProperty('--marker-color', color || '#ff6b00');

  const icon = document.createElement('div');
  icon.className = 'map-marker__icon';
  icon.innerHTML = MARKER_ICONS[kind] || MARKER_ICONS.default;
  wrapper.appendChild(icon);

  if (label) {
    const labelEl = document.createElement('div');
    labelEl.className = 'map-marker__label';
    labelEl.textContent = label;
    wrapper.appendChild(labelEl);
  }

  return wrapper;
}

export default function TrackingMap({
  courierLocation,
  courierLabel,
  restaurantLocation,
  restaurantLabel,
  customerLocation,
  customerLabel,
  customerKind,
  route,
  routes,
  extraMarkers,
  height = 280,
  fitPadding = 50,
  mapStyleUrl,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const lastFitKeyRef = useRef('');
  const initialCenterRef = useRef(null);

  const normalizedRoutes = useMemo(() => {
    const list = Array.isArray(routes) && routes.length ? routes : (route ? [route] : []);
    return list.map(normalizeRoute).filter(Boolean);
  }, [route, routes]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const container = containerRef.current;
    if (!initialCenterRef.current) {
      initialCenterRef.current = toLngLat(customerLocation) || toLngLat(restaurantLocation) || [-52.95, -22.55];
    }

    const map = new maplibregl.Map({
      container,
      style: mapStyleUrl || MAP_STYLE_URL,
      center: initialCenterRef.current,
      zoom: 12,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');

    mapRef.current = map;

    return () => {
      const instance = mapRef.current;
      mapRef.current = null;
      markersRef.current = {};

      if (instance) {
        try {
          instance.remove();
        } catch {
          // ignore remove errors on unmount
        }
      }

      try {
        if (container && container.isConnected) {
          container.innerHTML = '';
        }
      } catch {
        // ignore
      }
    };
  }, [mapStyleUrl]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const nextStyle = mapStyleUrl || MAP_STYLE_URL;
    const currentStyle = map.getStyle?.();
    const currentSprite = currentStyle?.sprite || '';
    if (currentSprite && nextStyle === MAP_STYLE_URL && currentSprite.includes('positron')) return;
    if (currentSprite && nextStyle === MAP_STYLE_URL_DARK && currentSprite.includes('dark-matter')) return;

    try {
      map.setStyle(nextStyle);
    } catch {
      // ignore style swap errors; map will keep current style
    }
  }, [mapStyleUrl]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const setMarker = (key, coords, options = {}) => {
      const lngLat = toLngLat(coords);
      if (!lngLat) {
        if (markersRef.current[key]?.marker) {
          markersRef.current[key].marker.remove();
          delete markersRef.current[key];
        }
        return;
      }

      const { color, kind, label } = options;
      const existing = markersRef.current[key];
      const needsUpdate = !existing || existing.kind !== kind || existing.label !== label || existing.color !== color;

      if (needsUpdate) {
        if (existing?.marker) {
          existing.marker.remove();
        }
        const element = buildMarkerElement({ kind, label, color });
        const marker = new maplibregl.Marker({ element, anchor: 'bottom' }).setLngLat(lngLat).addTo(map);
        markersRef.current[key] = { marker, kind, label, color };
      } else if (existing?.marker) {
        existing.marker.setLngLat(lngLat);
      }
    };

    setMarker('courier', courierLocation, { color: '#111827', kind: 'courier', label: courierLabel });
    setMarker('restaurant', restaurantLocation, { color: '#16a34a', kind: 'pickup', label: restaurantLabel });
    setMarker('customer', customerLocation, { color: '#2563eb', kind: customerKind || 'delivery_home', label: customerLabel });

    const extras = Array.isArray(extraMarkers) ? extraMarkers : [];
    const nextExtraKeys = new Set();
    extras.forEach((marker, index) => {
      const key = `extra-${marker.id || index}`;
      nextExtraKeys.add(key);
      setMarker(key, marker, {
        color: marker.color || '#2563eb',
        kind: marker.kind || 'delivery_home',
        label: marker.label,
      });
    });

    Object.keys(markersRef.current).forEach((key) => {
      if (key.startsWith('extra-') && !nextExtraKeys.has(key)) {
        markersRef.current[key].marker.remove();
        delete markersRef.current[key];
      }
    });

    const coords = [courierLocation, restaurantLocation, customerLocation]
      .map(toLngLat)
      .filter(Boolean);

    normalizedRoutes.forEach((feature) => {
      const routeCoords = feature?.geometry?.coordinates || feature?.coordinates;
      if (Array.isArray(routeCoords) && routeCoords.length > 1) {
        coords.push(routeCoords[0]);
        coords.push(routeCoords[routeCoords.length - 1]);
      }
    });

    const fitKey = JSON.stringify(coords);
    if (coords.length > 0 && fitKey !== lastFitKeyRef.current) {
      lastFitKeyRef.current = fitKey;
      const bounds = coords.reduce((acc, lngLat) => acc.extend(lngLat), new maplibregl.LngLatBounds(coords[0], coords[0]));
      map.fitBounds(bounds, { padding: fitPadding, duration: 500 });
    }
  }, [courierLocation, customerLocation, restaurantLocation, normalizedRoutes, extraMarkers, fitPadding]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const updateRoute = () => {
      const routeData = normalizedRoutes.length
        ? { type: 'FeatureCollection', features: normalizedRoutes }
        : { type: 'FeatureCollection', features: [] };

      const existing = map.getSource(ROUTE_SOURCE_ID);
      if (existing) {
        existing.setData(routeData);
        return;
      }

      map.addSource(ROUTE_SOURCE_ID, {
        type: 'geojson',
        data: routeData,
      });

      map.addLayer({
        id: ROUTE_LAYER_ID,
        type: 'line',
        source: ROUTE_SOURCE_ID,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#ff6b00'],
          'line-width': 4,
          'line-opacity': 0.85,
        },
      });
    };

    if (map.isStyleLoaded()) {
      updateRoute();
      return;
    }

    const handleLoad = () => updateRoute();
    map.once('load', handleLoad);

    return () => {
      if (map) {
        map.off('load', handleLoad);
      }
    };
  }, [normalizedRoutes, mapStyleUrl]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const timer = setTimeout(() => {
      try {
        map.resize();
      } catch {
        // ignore resize errors
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [height]);

  return (
    <div style={{ width: '100%', height, position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
